'use client'

import { Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { LighterSubaccountPicker } from '@/features/dashboard/components/account-connection/lighter-subaccount-picker'
import { ACCOUNT_CONNECTION_SHEET_CLASS } from '@/features/dashboard/components/account-connection/styles'
import { useLighterOnboard } from '@/hooks/use-lighter-onboard'
import { connectWallet } from '@/lib/lighter/sign-subaccount-key'
import {
  canSubmit,
  empty,
  ORDERX_API_KEY_INDEX,
  type SelectionState,
} from '@/lib/lighter/subaccount-selection'
import type { LighterSubaccountInfo } from '@/types/generated/public_api'

// Rotates the key for ONE already-linked Lighter account whose key went
// AUTH_FAILED. Reuses `lighterLinkSubaccounts` — the same RPC a fresh link
// uses — the backend treats a caller re-submitting a slot it already owns as
// a valid claim and unconditionally overwrites the stored credentials,
// resetting status to PENDING_AUTH for the trade-venue watcher to
// re-verify (see Task 14's plan note in oems_v2 for the backend trace).
//
// `PublicAccount` (the type `item` in account.tsx is) never carries
// `lighter_account_index`/`lighter_api_key_index` — those fields exist only
// on the internal `Account` message, not the public one returned to the
// frontend — so this sheet can't read them off the broken account directly.
// It re-derives the account's own slot via `lighterListSubaccounts`, the
// same list RPC the fresh-link wizard calls, filtering for the one entry
// whose `linkedAccountId` matches this `accountId` (populated only when
// linked by the calling account, per docs/lighter-frontend.md Step 1) — if
// nothing matches, the connected wallet isn't the one that originally linked
// this account, and that's surfaced as an error rather than guessed at.
//
// Once that slot is found, the selection is seeded directly (bypassing
// `toggleSlot`) with the found `accountIndex` and `ORDERX_API_KEY_INDEX` —
// the only slot the frontend ever mints (same constant `toggleSlot` uses) —
// so `toggleSlot`'s `alreadyLinked` guard (which exists to stop claiming a
// DIFFERENT account's slot) never blocks rotating this account's own slot.
// Accounts linked before that constant existed sit in slot 0, so a relink is
// also how they migrate off Lighter's reserved range.
type LighterKeyRelinkSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: string
  onComplete: () => void | Promise<void>
}

export function LighterKeyRelinkSheet({
  open,
  onOpenChange,
  accountId,
  onComplete,
}: LighterKeyRelinkSheetProps) {
  const t = useTranslations('accountConnection')
  const { listSubaccounts, linkSubaccounts } = useLighterOnboard()
  const [l1Address, setL1Address] = useState('')
  const [subaccount, setSubaccount] = useState<LighterSubaccountInfo | null>(null)
  const [selection, setSelection] = useState<SelectionState>(empty)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setError('')
    setSubaccount(null)
    setSelection(empty())
    setLoading(true)
    connectWallet()
      .then(async (address) => {
        if (cancelled) return
        setL1Address(address)
        const list = await listSubaccounts(accountId, address)
        if (cancelled) return
        const mine = list.subaccounts.find(
          (s) => s.alreadyLinked && s.linkedAccountId === accountId
        )
        if (!mine) {
          setError(t('lighterRelinkNotFound'))
          return
        }
        setSubaccount(mine)
        setSelection({
          slots: [
            {
              accountIndex: mine.accountIndex,
              apiKeyIndex: ORDERX_API_KEY_INDEX,
              keySource: { type: 'none' },
            },
          ],
        })
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, accountId, listSubaccounts, t])

  const handleSubmit = async () => {
    if (!canSubmit(selection)) return
    setSubmitting(true)
    setError('')
    try {
      const result = await linkSubaccounts(accountId, l1Address, selection)
      const slot = result.slots.find((s) => s.accountIndex === subaccount?.accountIndex)
      // A MISSING slot is a failure, not a success. If the backend returns an
      // empty `slots` (or echoes an accountIndex we didn't send), treating it
      // as ok fires onComplete's "Reconnection submitted" toast and closes the
      // sheet — telling the trader their AUTH_FAILED account is fixed when
      // nothing was relinked. `error` is a proto3 string defaulting to '', so
      // it needs a fallback of its own or the Error message is empty.
      if (!slot || !slot.ok) throw new Error(slot?.error || t('lighterLinkError'))
      await onComplete()
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className={ACCOUNT_CONNECTION_SHEET_CLASS}>
        <SheetHeader>
          <SheetTitle>{t('lighterManageSubaccounts')}</SheetTitle>
        </SheetHeader>
        {error && <p className="text-chart-red text-sm">{error}</p>}
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-white/60">
            <Loader2Icon className="size-4 animate-spin" />
            <span>{t('preparing')}</span>
          </div>
        ) : (
          subaccount && (
            <LighterSubaccountPicker
              subaccounts={[subaccount]}
              accountId={accountId}
              l1Address={l1Address}
              selection={selection}
              onChange={setSelection}
              onSubmit={handleSubmit}
              submitting={submitting}
              // A relink re-keys a slot the account already occupies, so it
              // consumes no new plan slot — the single row is always allowed.
              remainingSlots={1}
            />
          )
        )}
      </SheetContent>
    </Sheet>
  )
}
